import React, { useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import moment from 'moment';
import { compose } from 'redux';

// HELPERS
import { withRouter } from 'helpers/withRouter';

// GLOBAL VARIABLES
import { NEW_USED } from 'codes';
import { INVENTORY_FILE_TYPE_IDS } from 'globals.js';

// GLOBAL COMPONENTS
import Card from 'parts/card/Card';
import Form from 'core/form/Form';

// LOCAL VARIABLES
import { INVENTORY_SOURCE_IDS, LOOKUP_PARAMS, INVENTORY_SOURCE_OPTION_KEYS, SALES_USER_OPTION_KEYS } from './variables';

// LOCAL COMPONENTS
import Attachment from 'routers/interface/tools/attachment/Attachment';

// GLOBAL FUNCTIONS
import { formatDateTime } from 'functions.js';

// MAIN COMPONENT
const PurchaseInfoBody = compose(withRouter)(({ location: { search } }) => {
  const { values } = useFormikContext() || {};
  const {
    new_used,
    inventory_source_id,
    inventory_source_method_name,
    located_at_dealership,
    date_created,
    created_by,
    purchase_date
  } = values;
  const isPurchaseFromCustomer = +inventory_source_id === INVENTORY_SOURCE_IDS.PURCHASE_FROM_CUSTOMER_FOR_INVENTORY;
  const isManufacturer = +inventory_source_id === INVENTORY_SOURCE_IDS.MANUFACTURER;
  const isTrade = +inventory_source_id === INVENTORY_SOURCE_IDS.TRADE;
  const params = new URLSearchParams(search);
  const newUsedParam = params.get('new_used');
  const newUsed = newUsedParam ? +newUsedParam : new_used;
  const isNew = newUsed === NEW_USED.new.numeric;
  const lookupParams = useMemo(
    () => ({
      inventorySource: {
        newUsed: newUsed || '0',
      },
      inventoryLocation: {
        newUsed: newUsed,
        vehicleAtDealership: located_at_dealership
      }
    }),
    [newUsed, located_at_dealership]
  );

  //RENDER
  return (
    <Form.Body>
      <Form.Control
        label="Purchase Date"
        value={formatDateTime(purchase_date, 4, true)}
        text="* Must be the date on the Seller's Bill of Sale"
        plaintext
      />
      <Form.PlainText
        label="Source Method"
        value={`${inventory_source_method_name} ${moment(date_created).format('MM/DD/YYYY')} at ${moment(
          date_created
        ).format('hh:mm A')} by ${created_by}`}
      />
      {isTrade ? (
        <>
          <Form.Hidden name="inventory_source_id" />
          <Form.PlainText label="Source" name="inventory_source" />
        </>
      ) : (
        <Form.Select
          name="inventory_source_id"
          label="Source"
          lookup={isNew ? 'inventorySourceNew' : 'inventorySourceUsed'}
          lookupParams={lookupParams.inventorySource}
          optionKeys={INVENTORY_SOURCE_OPTION_KEYS}
          required
        />
      )}
      <Form.Control
        name="source_name"
        label="Source Name"
        hidden={isNew && +inventory_source_id !== 6 && +inventory_source_id !== 8}
        plaintext={isTrade}
      />
      <Form.Select
        name="buyer_user_id"
        label="Acquired By - Sales Manager"
        lookup="salesManagers"
        lookupParams={LOOKUP_PARAMS.salesManagerStaff}
        optionKeys={SALES_USER_OPTION_KEYS}
        required={!isManufacturer && !isTrade}
        hidden={(isNew && !+inventory_source_id) || isManufacturer || isTrade}
      />
      <Form.Select
        name="acquired_salesperson_user_id"
        label="Acquired By - Salesperson (If Any)"
        lookup="salesStaff"
        lookupParams={LOOKUP_PARAMS.salesStaff}
        optionKeys={SALES_USER_OPTION_KEYS}
        dropdown={{ maxHeight: '30rem' }}
        hidden={!isPurchaseFromCustomer}
      />
      <Form.Select
        name="appraised_by_user_id"
        label="Appraised By"
        lookup="salesManagers"
        lookupParams={LOOKUP_PARAMS.salesManagerStaff}
        optionKeys={SALES_USER_OPTION_KEYS}
        dropdown={{ maxHeight: '30rem' }}
        hidden={!isTrade}
        useNone
      />
      <Attachment.Upload
        label="Trade Appraisal"
        name="documentAttachment.trade_appraisal"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_APPRAISAL_USED}
        hidden={!isTrade}
      />
      <Attachment.Upload
        label="Trade Inspection"
        name="documentAttachment.trade_inspection"
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_INSPECTION}
        hidden={!isTrade}
      />
      <Attachment.Upload
        label="Title in Possession"
        name="documentAttachment.trade_title"
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_POSESSION}
        hidden={!isTrade}
      />
      <Attachment.Upload
        label="Vehicle Registration"
        name="documentAttachment.trade_registration"
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_REGISTRATION}
        hidden={!isTrade}
      />
    </Form.Body>
  );
});

// EXPORT
export default function PurchaseInfo(props) {
  return <Card title="Purchase Info" body={<PurchaseInfoBody {...props} />} />;
}
